import { useMemo } from 'react';
import dayjs from 'dayjs';

/**
 * Custom hook for date calculations and date object management
 * @param {Object} props - Component props containing startDate and endDate
 * @returns {Object} Memoized date objects and calculations
 */
export const useDateCalculations = (props) => {
  // Memoize date objects to prevent unnecessary recalculations
  const startDate = useMemo(() => {
    return dayjs.isDayjs(props.startDate) ? props.startDate.clone() : dayjs(props.startDate);
  }, [props.startDate]);

  const endDate = useMemo(() => {
    return dayjs.isDayjs(props.endDate) ? props.endDate.clone() : dayjs(props.endDate);
  }, [props.endDate]);

  // Memoize date calculations to avoid recalculating on every render
  const dateCalculations = useMemo(() => {
    const startDayMonth = startDate.format("D");
    const endDayMonth = endDate.format("D");
    const lastDayMonth = endDate.endOf("month").date();
    const monthsSelected = endDate.diff(startDate, "months") + 1;
    const daysSelected = endDate.diff(startDate, "day") + 1;
    const isCompleteMonthSelection =
      Number(startDayMonth) === 1 && Number(endDayMonth) === lastDayMonth;

    return {
      startDayMonth,
      endDayMonth,
      lastDayMonth,
      monthsSelected,
      daysSelected,
      isCompleteMonthSelection
    };
  }, [startDate, endDate]);

  // Memoize RangePicker values to prevent unnecessary re-renders
  const rangePickerValue = useMemo(() => [
    startDate,
    endDate
  ], [startDate, endDate]);

  return {
    startDate,
    endDate,
    dateCalculations,
    rangePickerValue
  };
};
